import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CalendarDays, CalendarPlus, CheckSquare, FileSearch, Loader2, MessageSquare, Users,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  CATEGORIA_TAREFA_LABEL, PRIORIDADE_LABEL, SITUACAO_CONSULTA_LABEL,
  TIPO_CONSULTA_LABEL, type Clinica,
} from '@/lib/types'
import { formatarData, formatarDataLonga, hojeISO, hora } from '@/lib/datas'
import { listClinicas } from '@/services/clinicas'
import { listConsultas, type ConsultaComPaciente } from '@/services/consultas'
import { listPacientes } from '@/services/pacientes'
import { listTarefas, mudarStatusTarefa, type TarefaComRelacoes } from '@/services/tarefas'
import { listDocumentosParaAnalisar, STATUS_DOC_LABEL, type Documento } from '@/services/documentos'
import { CATEGORIA_MSG_LABEL, listThreads, type ThreadComPaciente } from '@/services/mensagens'

/**
 * Tela "Hoje" — home do Médico 360.
 * Todos os blocos são reais e acionáveis (Etapas 2-6).
 */

export default function HojePage() {
  const { perfil, current, user, papel } = useAuth()
  const [consultas, setConsultas] = useState<ConsultaComPaciente[]>([])
  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [tarefasDoDia, setTarefasDoDia] = useState<TarefaComRelacoes[]>([])
  const [totalAbertas, setTotalAbertas] = useState(0)
  const [docsAnalisar, setDocsAnalisar] = useState<(Documento & { paciente_nome: string })[]>([])
  const [threadsAguardando, setThreadsAguardando] = useState<ThreadComPaciente[]>([])
  const [totalPacientes, setTotalPacientes] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    try {
      const hoje = hojeISO()
      const [cons, cs, ps] = await Promise.all([
        listConsultas({ workspaceId: current.workspace.id, de: hoje, ate: hoje }),
        listClinicas(current.workspace.id),
        listPacientes(current.workspace.id, { situacao: 'ativa' }),
      ])
      setConsultas(cons.filter(c => c.situacao !== 'cancelada'))
      setClinicas(cs)
      setTotalPacientes(ps.length)
      try {
        const abertas = await listTarefas(current.workspace.id, { aba: 'abertas' })
        setTotalAbertas(abertas.length)
        setTarefasDoDia(abertas.filter(t => t.prazo && t.prazo <= hoje).slice(0, 6))
      } catch {
        // phase3 ainda não aplicada — bloco degrada para o estado vazio
      }
      try {
        setDocsAnalisar(await listDocumentosParaAnalisar(current.workspace.id))
      } catch {
        // phase5 ainda não aplicada
      }
      try {
        const abertas = await listThreads(current.workspace.id, { aba: 'abertas' })
        const minhas = papel === 'medico_titular' || papel === 'medico_colaborador'
          ? abertas.filter(t => ['aguardando_medico', 'aguardando_aprovacao'].includes(t.status))
          : abertas.filter(t => ['recebida', 'em_triagem', 'aguardando_secretaria'].includes(t.status))
        setThreadsAguardando(minhas.slice(0, 5))
      } catch {
        // phase7 ainda não aplicada
      }
    } catch {
      // tabelas da fase 2 podem ainda não existir — a home continua utilizável
      setTotalPacientes(null)
    } finally {
      setLoading(false)
    }
  }, [current, papel])

  async function concluirRapido(t: TarefaComRelacoes) {
    if (!current) return
    await mudarStatusTarefa({
      workspaceId: current.workspace.id,
      tarefa: t,
      novoStatus: 'concluida',
      notificarUserId: t.criado_por && t.criado_por !== user?.id ? t.criado_por : null,
    })
    await carregar()
  }

  useEffect(() => { carregar() }, [carregar])

  // "Dra. Teste da Silva" → "Dra. Teste" (título sozinho não é saudação)
  const partes = perfil?.nome?.split(' ').filter(Boolean) ?? []
  const primeiroNome = /^dra?\.?$/i.test(partes[0] ?? '')
    ? partes.slice(0, 2).join(' ')
    : partes[0]

  const nomeClinica = (id: string | null) => clinicas.find(c => c.id === id)?.nome
  const corClinica = (id: string | null) => clinicas.find(c => c.id === id)?.cor ?? 'var(--muted)'

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <div className="page-subtitle" style={{ textTransform: 'capitalize' }}>{formatarDataLonga(hojeISO())}</div>
        <h1 className="page-title" style={{ margin: '2px 0 0' }}>
          {primeiroNome ? `Hoje, ${primeiroNome}` : 'Hoje'}
        </h1>
        {current && (
          <div className="page-subtitle" style={{ marginTop: 2 }}>{current.workspace.nome}</div>
        )}
      </header>

      {/* Consultas de hoje — bloco real e acionável */}
      <section className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div aria-hidden style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--primary-soft)', color: 'var(--primary-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarDays size={17} />
            </div>
            <strong style={{ fontSize: 15 }}>Consultas de hoje</strong>
            {!loading && <span className="badge badge-info">{consultas.length}</span>}
          </div>
          <Link to="/agenda/nova" className="btn-secondary" style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }}>
            <CalendarPlus size={14} aria-hidden /> Agendar
          </Link>
        </div>

        {loading ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--muted)' }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
            <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
          </div>
        ) : consultas.length === 0 ? (
          <div style={{ fontSize: 13.5, color: 'var(--muted)', padding: '6px 0' }}>
            Sem consultas hoje. Aproveite para colocar as pendências em dia.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {consultas.map(c => (
              <Link key={c.id} to={`/agenda/nova?id=${c.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border)' }}>
                <span aria-hidden style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: corClinica(c.clinica_id) }} />
                <strong style={{ minWidth: 48, fontSize: 14.5 }}>{hora(c.hora_inicio)}</strong>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.paciente_nome}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {TIPO_CONSULTA_LABEL[c.tipo]}{nomeClinica(c.clinica_id) ? ` · ${nomeClinica(c.clinica_id)}` : ''}
                  </div>
                </div>
                <span className={`badge ${c.situacao === 'realizada' ? 'badge-ok' : 'badge-info'}`}>
                  {SITUACAO_CONSULTA_LABEL[c.situacao]}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Pendências que vencem hoje ou estão atrasadas — acionável */}
      <section className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div aria-hidden style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--primary-soft)', color: 'var(--primary-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckSquare size={17} />
            </div>
            <strong style={{ fontSize: 15 }}>Pendências de hoje</strong>
            {!loading && totalAbertas > 0 && <span className="badge badge-info">{totalAbertas} abertas</span>}
          </div>
          <Link to="/pendencias" className="btn-secondary" style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }}>
            Ver todas
          </Link>
        </div>
        {loading ? null : tarefasDoDia.length === 0 ? (
          <div style={{ fontSize: 13.5, color: 'var(--muted)', padding: '6px 0' }}>
            Nada vencendo hoje. {totalAbertas > 0 ? `${totalAbertas} pendência${totalAbertas > 1 ? 's' : ''} aberta${totalAbertas > 1 ? 's' : ''} com prazo futuro.` : ''}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tarefasDoDia.map(t => {
              const atrasada = t.prazo !== null && t.prazo < hojeISO()
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`/pendencias/nova?id=${t.id}`} style={{ fontWeight: 600, fontSize: 14 }}>{t.titulo}</Link>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {CATEGORIA_TAREFA_LABEL[t.categoria]}
                      {t.paciente_nome ? ` · ${t.paciente_nome}` : ''}
                      {t.prioridade !== 'normal' ? ` · ${PRIORIDADE_LABEL[t.prioridade]}` : ''}
                    </div>
                  </div>
                  {t.prazo && (
                    <span className={`badge ${atrasada ? 'badge-danger' : 'badge-warn'}`}>
                      {atrasada ? 'Atrasada' : formatarData(t.prazo)}
                    </span>
                  )}
                  <button
                    className="btn-secondary" style={{ minHeight: 34, padding: '4px 10px', fontSize: 12.5 }}
                    onClick={() => concluirRapido(t)} aria-label={`Concluir ${t.titulo}`}
                  >
                    Concluir
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Exames/documentos aguardando análise */}
      {docsAnalisar.length > 0 && (
        <section className="card" style={{ padding: 18, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div aria-hidden style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--primary-soft)', color: 'var(--primary-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileSearch size={17} />
            </div>
            <strong style={{ fontSize: 15 }}>Exames para analisar</strong>
            <span className="badge badge-warn">{docsAnalisar.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docsAnalisar.slice(0, 5).map(d => (
              <Link key={d.id} to={`/pacientes/${d.paciente_id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{d.nome}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {d.paciente_nome}{d.origem === 'paciente' ? ' · enviado pela paciente' : ''}
                  </div>
                </div>
                <span className="badge badge-warn">{STATUS_DOC_LABEL[d.status]}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        <Link to="/pacientes" className="card" style={{ padding: 18, display: 'block' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div aria-hidden style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--primary-soft)', color: 'var(--primary-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={17} />
            </div>
            <strong style={{ fontSize: 15 }}>Carteira de pacientes</strong>
            {totalPacientes !== null && <span className="badge badge-info">{totalPacientes} ativas</span>}
          </div>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Qual paciente precisa de atenção agora?</p>
        </Link>

        <div className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div aria-hidden style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--primary-soft)', color: 'var(--primary-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageSquare size={17} />
            </div>
            <strong style={{ fontSize: 15 }}>Mensagens aguardando</strong>
            {!loading && threadsAguardando.length > 0 && <span className="badge badge-warn">{threadsAguardando.length}</span>}
          </div>
          {threadsAguardando.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)' }}>
              Nenhuma mensagem esperando por você. <Link to="/mensagens" style={{ color: 'var(--primary-deep)', fontWeight: 600 }}>Ver central</Link>
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {threadsAguardando.map(t => (
                <Link key={t.id} to={`/mensagens/${t.id}`} style={{ fontSize: 13.5, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>{t.paciente_nome}</span>
                  <span style={{ color: 'var(--muted)' }}>· {CATEGORIA_MSG_LABEL[t.categoria]}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
